import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  CalendarPlus,
  User,
  Wrench,
  Calendar,
  Clock,
  ArrowLeft,
  Loader2,
  Check,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddCustomerService = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [venues, setVenues] = useState([]);
  const [workers, setWorkers] = useState([]);

  const [formData, setFormData] = useState({
    customer_id: "",
    installation_id: "",
    venue_id: "",
    service_id: "",
    booking_date: "",
    start_time: "09:00",
    end_time: "11:00",
    assigned_worker_id: "",
    notes: "",
    expected_return_date: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersRes, servicesRes, usersRes] = await Promise.all([
          axios.get(`${API}/customers`, { withCredentials: true }),
          axios.get(`${API}/services`, { withCredentials: true }),
          axios.get(`${API}/users/workers`, { withCredentials: true }),
        ]);
        setCustomers(customersRes.data);
        setServices(servicesRes.data);
        setWorkers(usersRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch installations and venues when customer changes
  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!formData.customer_id) {
        setInstallations([]);
        setVenues([]);
        return;
      }
      try {
        const [instRes, venuesRes] = await Promise.all([
          axios.get(`${API}/customers/${formData.customer_id}/installations`, { withCredentials: true }),
          axios.get(`${API}/customers/${formData.customer_id}/venues`, { withCredentials: true }),
        ]);
        setInstallations(instRes.data);
        setVenues(venuesRes.data);
        // Auto-select if only one installation
        if (instRes.data.length === 1) {
          setFormData(prev => ({ ...prev, installation_id: instRes.data[0].installation_id }));
        }
      } catch (error) {
        console.error("Error fetching customer data:", error);
      }
    };
    fetchCustomerData();
  }, [formData.customer_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id || !formData.service_id || !formData.booking_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const customer = customers.find(c => c.customer_id === formData.customer_id);
      const service = services.find(s => s.service_id === formData.service_id);
      const installation = formData.installation_id ? installations.find(i => i.installation_id === formData.installation_id) : null;
      const worker = workers.find(w => w.user_id === formData.assigned_worker_id);

      const bookingData = {
        customer_id: formData.customer_id,
        customer_name: customer?.name || "",
        installation_id: formData.installation_id || null,
        installation_name: installation?.location_name || "",
        service_id: formData.service_id,
        service_name: service?.name || "",
        booking_date: formData.booking_date,
        time_slot: {
          start_time: formData.start_time,
          end_time: formData.end_time,
        },
        notes: formData.notes,
        status: "confirmed",
        assigned_worker_id: formData.assigned_worker_id || null,
        assigned_worker_name: worker?.name || null,
        venue_id: formData.venue_id || null,
        expected_return_date: service?.creates_rental ? (formData.expected_return_date || null) : null,
      };

      const res = await axios.post(`${API}/service-bookings`, bookingData, { withCredentials: true });
      if (res.data?.linked_rental_id) {
        toast.success("Service booked — rental auto-created with return schedule");
      } else {
        toast.success("Service booking created successfully");
      }
      navigate("/service-calendar");
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="h-96 bg-slate-200 rounded-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="add-customer-service-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <CalendarPlus className="h-7 w-7" />
            Add Customer Service
          </h1>
          <p className="text-slate-500 mt-1">
            Book a service appointment for a customer
          </p>
        </div>
      </div>

      <Card className="rounded-sm border-slate-200 max-w-2xl">
        <CardHeader>
          <CardTitle className="text-lg">Booking Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Selection */}
            <div className="space-y-2">
              <Label htmlFor="customer" className="flex items-center gap-2">
                <User className="h-4 w-4 text-slate-400" />
                Customer <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.customer_id}
                onValueChange={(value) => setFormData({ ...formData, customer_id: value, installation_id: "", venue_id: "" })}
              >
                <SelectTrigger className="rounded-sm" data-testid="customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.customer_id} value={customer.customer_id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Venue Selection — only shown when customer has venues */}
            {formData.customer_id && venues.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  Venue <span className="text-slate-400 text-xs">(optional)</span>
                </Label>
                <Select
                  value={formData.venue_id}
                  onValueChange={(value) => setFormData({ ...formData, venue_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select venue (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific venue</SelectItem>
                    {venues.map((venue) => (
                      <SelectItem key={venue.venue_id} value={venue.venue_id}>
                        {venue.venue_name}{venue.address?.city ? ` — ${venue.address.city}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Installation Selection */}
            {formData.customer_id && (
              <div className="space-y-2">
                <Label htmlFor="installation">
                  Installation <span className="text-slate-400 text-xs">(optional)</span>
                </Label>
                <Select
                  value={formData.installation_id}
                  onValueChange={(value) => setFormData({ ...formData, installation_id: value })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="installation-select">
                    <SelectValue placeholder="Select installation (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {installations.length > 0 ? (
                      installations.map((inst) => (
                        <SelectItem key={inst.installation_id} value={inst.installation_id}>
                          {inst.location_name || inst.system_name || "Installation"}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>
                        No installations found
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Service Selection */}
            <div className="space-y-2">
              <Label htmlFor="service" className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-slate-400" />
                Service <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.service_id}
                onValueChange={(value) => setFormData({ ...formData, service_id: value })}
              >
                <SelectTrigger className="rounded-sm" data-testid="service-select">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.service_id} value={service.service_id}>
                      {service.name} {!service.customer_bookable && "(Internal)"}
                      {service.creates_rental && " · Rental"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Rental return schedule — shown when selected service is a rental service */}
            {services.find(s => s.service_id === formData.service_id)?.creates_rental && (
              <div className="space-y-2 p-3 rounded-sm bg-amber-50 border border-amber-200">
                <Label htmlFor="expected_return_date" className="flex items-center gap-2 text-amber-900">
                  <Calendar className="h-4 w-4" />
                  Expected Return Date
                </Label>
                <Input
                  id="expected_return_date"
                  type="date"
                  value={formData.expected_return_date}
                  onChange={(e) => setFormData({ ...formData, expected_return_date: e.target.value })}
                  className="rounded-sm bg-white"
                  min={formData.booking_date || new Date().toISOString().split("T")[0]}
                  data-testid="expected-return-date-input"
                />
                <p className="text-xs text-amber-800">
                  This service will automatically create a rental record with a return schedule.
                  Leave blank to use the service default duration.
                </p>
              </div>
            )}

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.booking_date}
                  onChange={(e) => setFormData({ ...formData, booking_date: e.target.value })}
                  className="rounded-sm"
                  min={new Date().toISOString().split("T")[0]}
                  data-testid="date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="start_time" className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  Start Time
                </Label>
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="rounded-sm"
                  data-testid="start-time-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_time">End Time</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="rounded-sm"
                  data-testid="end-time-input"
                />
              </div>
            </div>

            {/* Worker Assignment */}
            <div className="space-y-2">
              <Label htmlFor="worker">Assign Technician (Optional)</Label>
              <Select
                value={formData.assigned_worker_id}
                onValueChange={(value) => setFormData({ ...formData, assigned_worker_id: value })}
              >
                <SelectTrigger className="rounded-sm" data-testid="worker-select">
                  <SelectValue placeholder="Select technician (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No assignment</SelectItem>
                  {workers.map((worker) => (
                    <SelectItem key={worker.user_id} value={worker.user_id}>
                      {worker.name} ({worker.role === "worker" ? "Technician" : "Manager"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Add any notes for this booking..."
                className="rounded-sm"
                rows={3}
                data-testid="notes-input"
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-sm bg-indigo-600 hover:bg-indigo-700"
                data-testid="submit-booking-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Create Booking
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddCustomerService;
